/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-redeclare */
/* eslint-disable regex/invalid */
import {
    type HTMLAttributes,
    type DetailedReactHTMLElement,
    type ReactNode,
    type ReactHTMLElement,
    type ReactSVGElement,
    type DOMAttributes,
    type DOMElement,
    type FunctionComponentElement,
    type Component,
    type ComponentState,
    type ReactElement,
    type SVGAttributes,
    type ClassAttributes,
    type CElement,
    type Attributes,
    cloneElement,
} from 'react';
import { WidgetMetaProps } from '@alfa-bank/site-platform-types/types/widgets';

// DOM Elements
// ReactHTMLElement
export function cloneWidget<P extends HTMLAttributes<T>, T extends HTMLElement>(
    element: DetailedReactHTMLElement<P, T>,
    props?: P,
    ...children: ReactNode[]
): DetailedReactHTMLElement<P, T>;
// ReactHTMLElement, less specific
export function cloneWidget<P extends HTMLAttributes<T>, T extends HTMLElement>(
    element: ReactHTMLElement<T>,
    props?: P,
    ...children: ReactNode[]
): ReactHTMLElement<T>;
// SVGElement
export function cloneWidget<P extends SVGAttributes<T>, T extends SVGElement>(
    element: ReactSVGElement,
    props?: P,
    ...children: ReactNode[]
): ReactSVGElement;
// DOM Element (has to be the last, because type checking stops at first overload that fits)
export function cloneWidget<P extends DOMAttributes<T>, T extends Element>(
    element: DOMElement<P, T>,
    props?: DOMAttributes<T> & P,
    ...children: ReactNode[]
): DOMElement<P, T>;

// Custom components
export function cloneWidget<P>(
    element: FunctionComponentElement<P>,
    props?: Partial<P> & Attributes,
    ...children: ReactNode[]
): FunctionComponentElement<P>;
export function cloneWidget<P, T extends Component<P, ComponentState>>(
    element: CElement<P, T>,
    props?: Partial<P> & ClassAttributes<T>,
    ...children: ReactNode[]
): CElement<P, T>;
export function cloneWidget<P>(
    element: ReactElement<P>,
    props?: Partial<P> & Attributes,
    ...children: ReactNode[]
): ReactElement<P>;

/**
 * Полный аналог cloneElement с таким же интерфейсом
 * удаляет из props meta данные добавляемые в ASSR-CORE
 * для корректной работы поиска в визивиг
 */
export function cloneWidget(
    element: any,
    props?: any,
    children?: any
): ReturnType<typeof cloneElement> {
    const args = [element];

    if (props) {
        /** Удаляем meta пропсы */
        Object.keys(WidgetMetaProps).forEach(key => delete props[key]);
        args.push(props);
    } else {
        args.push(undefined);
    }

    if (children) args.push(children);

    /** Если передавать на прямую cloneElement(element, props, children)
     *  например children undefined перезапишет чилды у element если они есть
     *  https://github.com/facebook/react/blob/246d7bfeb0c90ecccd9531929b60a79d628a4c78/packages/react/src/jsx/ReactJSXElement.js#L985
     */
    return cloneElement.call(null, ...(args as Parameters<typeof cloneElement>));
}
